import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

export type Position = [number, number];

interface DragHandlerConfig {
  /// the starting position for the content, as a [x, y] tuple
  initialPosition?: Position;
  /// the margin to keep between the draggable's content and the edge of the screen
  margin?: number;
  /// callback fired when dragging ends
  onDragEnd?: (x: number, y: number) => void;
}

interface DragHandlerResult<TContent extends HTMLElement, THandle extends HTMLElement> {
  /// whether or not the handle is being dragged.
  dragging: boolean;
  /// ref to include on the component that will be used as the handle for dragging. Note that the
  /// handle should be a child of the content, otherwise the handle may move independently of the
  /// content
  handleRef: (node: THandle) => void;
  /// ref to include on the component that represents the draggable's content.
  contentRef: (node: TContent) => void;
}

export function useDragHandler<
  TContent extends HTMLElement = HTMLDivElement,
  THandle extends HTMLElement = HTMLDivElement,
>({
  margin = 0,
  onDragEnd,
  initialPosition,
}: DragHandlerConfig): DragHandlerResult<TContent, THandle> {
  const contentRef = useRef<TContent | null>(null);
  const handleRef = useRef<THandle | null>(null);
  const startOffsetRef = useRef<Position>([0, 0]);
  const contentDimensRef = useRef({ width: 0, height: 0 });
  const posRef = useRef<Position>(initialPosition ?? [0, 0]);

  const [contentRefAvailable, setContentRefAvailable] = useState(false);
  const [dragging, setDragging] = useState(false);

  // handle mousing down on the handle
  const onMouseDown = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    if (contentRef.current == null) return;
    const { x, y, width, height } = contentRef.current.getBoundingClientRect();
    contentDimensRef.current = { width, height };
    startOffsetRef.current = [event.clientX - x, event.clientY - y];
    setDragging(true);
  }, []);

  // suppress clicking on the handle to ensure it doesn't trigger a drag
  const onClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  // handle the handle ref
  const setHandleRef = useCallback(
    (node: THandle | null) => {
      if (handleRef.current != null) {
        // cleanup old ref, if needed
        handleRef.current.removeEventListener("mousedown", onMouseDown);
        handleRef.current.removeEventListener("click", onClick);
      }

      // update the ref and mount event listeners, if needed
      handleRef.current = node;
      if (handleRef.current != null) {
        handleRef.current.addEventListener("mousedown", onMouseDown);
        handleRef.current.addEventListener("click", onClick);
      }
    },
    [onMouseDown, onClick]
  );

  // handle the content ref
  const setContentRef = useCallback((node: TContent | null) => {
    contentRef.current = node;
    setContentRefAvailable(contentRef.current != null);
    if (contentRef.current != null) {
      const { width, height } = contentRef.current.getBoundingClientRect();
      contentDimensRef.current = { width, height };
    }
  }, []);

  // get the bounds for the drag area
  const getBounds = useCallback((): Position => {
    return [
      window.innerWidth - contentDimensRef.current.width - margin,
      window.innerHeight - contentDimensRef.current.height - margin,
    ];
  }, [margin]);

  // handle updating the content position while dragging
  useEffect(() => {
    if (!dragging) return;

    const drag = (event: MouseEvent) => {
      event.preventDefault(); // prevents text selection while dragging

      if (contentRef.current == null) return;

      const [maxX, maxY] = getBounds();
      const x = _.clamp(event.clientX - startOffsetRef.current[0], margin, maxX);
      const y = _.clamp(event.clientY - startOffsetRef.current[1], margin, maxY);
      posRef.current = [x, y];
      contentRef.current.style.top = `${y}px`;
      contentRef.current.style.left = `${x}px`;
    };

    document.addEventListener("mousemove", drag);
    return () => document.removeEventListener("mousemove", drag);
  }, [dragging, getBounds, margin]);

  // handle cleanup once dragging has completed
  useEffect(() => {
    if (!dragging) return;

    const stop = () => {
      setDragging(false);
      onDragEnd?.(...posRef.current);
    };

    document.addEventListener("mouseup", stop);
    return () => document.removeEventListener("mouseup", stop);
  }, [dragging, onDragEnd]);

  // perform initial configuration of the content
  useEffect(() => {
    // need to wait for the content ref before positioning
    if (!contentRefAvailable || contentRef.current === null) return;
    contentRef.current.style.position = "absolute";
    contentRef.current.style.zIndex = "99";

    if (initialPosition != null) {
      const [maxX, maxY] = getBounds();
      const x = _.clamp(initialPosition[0], margin, maxX);
      const y = _.clamp(initialPosition[1], margin, maxY);
      posRef.current = [x, y];
      contentRef.current.style.top = `${y}px`;
      contentRef.current.style.left = `${x}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on initial mount
  }, [contentRefAvailable]);

  // Set position when `initialPosition` or `margin` change.
  useEffect(() => {
    // need to wait for the content ref before positioning
    if (!(contentRefAvailable && initialPosition && contentRef.current)) return;

    const [maxX, maxY] = getBounds();
    const x = _.clamp(initialPosition[0], margin, maxX);
    const y = _.clamp(initialPosition[1], margin, maxY);
    posRef.current = [x, y];
    contentRef.current.style.top = `${y}px`;
    contentRef.current.style.left = `${x}px`;
  }, [contentRefAvailable, getBounds, initialPosition, margin]);

  return {
    dragging,
    handleRef: setHandleRef,
    contentRef: setContentRef,
  };
}
